import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, MessageSquare, ShieldCheck } from 'lucide-react';
import Seo from '@/components/Seo';
import SmsConsentCheckbox from '@/components/SmsConsentCheckbox';
import { useI18n } from '@/i18n/LanguageContext';
import { SMS_MESSAGE_TYPES, SMS_OPT_IN_PAGE_COPY, SMS_PROGRAM_NAME, SMS_SUPPORT_EMAIL, smsConsentPayload } from '@/data/smsConsent';
import { submitForm } from '@/lib/crmForms';

/**
 * PRIMARY A2P 10DLC OPT-IN PAGE — https://giggone.com/en/sms-opt-in
 * (Spanish: /es/mensajes-de-texto). Registered in the Trust Center; do not
 * change the URL without updating the A2P registration documentation.
 *
 * The visitor enters a phone number and VOLUNTARILY ticks the UNCHECKED,
 * optional SmsConsentCheckbox (canonical disclosure). Nothing is submitted as
 * consent unless the box is ticked. crm-capture stores the verified evidence
 * row (user/contact id, phone, consent_type=sms, status, disclosure version +
 * exact text, language, form, page URL, timestamp, IP, user agent, GHL id).
 *
 * ?src=reoptin (link in the legacy re-opt-in email) only changes the intro text.
 */
const inputCls = 'mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200';

const SmsOptIn: React.FC = () => {
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const [params] = useSearchParams();
  const reoptin = params.get('src') === 'reoptin';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sms, setSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<'yes' | 'no' | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!sms) { setDone('no'); return; }
    if (phone.replace(/\D/g, '').length < 10) { setError(es ? 'Escriba su número de celular para recibir textos.' : 'Enter your mobile phone number to receive texts.'); return; }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { setError(es ? 'Escriba un correo válido o déjelo vacío.' : 'Enter a valid email or leave it blank.'); return; }
    setBusy(true);
    try {
      const res: any = await submitForm({
        formType: reoptin ? 'sms-reoptin' : 'sms-opt-in',
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim(),
        language: lang,
        consent: false, // SMS consent only — separate from marketing email consent
        ...smsConsentPayload(true),
        tags: [reoptin ? 'sms-reoptin' : 'sms-opt-in-page'],
      } as any);
      if (res && res.ok === false && res.error) throw new Error(res.message || res.error);
      setDone('yes');
    } catch {
      setError(es ? 'No pudimos guardar su elección. Intente de nuevo.' : 'We could not save your choice. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Copy is shared with the build-time crawler prerender (raw HTML must match).
  const c = SMS_OPT_IN_PAGE_COPY[es ? 'es' : 'en'];

  return (
    <div className="mx-auto grid max-w-5xl gap-8 px-4 py-12 sm:py-16 lg:grid-cols-5">
      <Seo title={c.seoTitle} description={c.seoDescription} />

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:col-span-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-orange-600"><MessageSquare className="h-5 w-5" /></span>
        <p className="mt-4 text-xs font-bold uppercase tracking-wider text-orange-600">{SMS_PROGRAM_NAME}</p>
        <h1 className="mt-1 text-2xl font-extrabold text-slate-900">{c.h1}</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{reoptin ? c.reoptinIntro : c.intro}</p>

        {done ? (
          <div className="mt-6 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-900" role="status">
            <p className="flex items-center gap-2 font-bold"><CheckCircle2 className="h-4 w-4" />{done === 'yes' ? (es ? 'Listo. Está suscrito a GIGGONE Alerts.' : 'Done. You are subscribed to GIGGONE Alerts.') : (es ? 'Entendido. No le enviaremos mensajes de texto.' : 'Understood. We will not text you.')}</p>
            {done === 'yes' && <p className="mt-1">{es ? 'Responda STOP en cualquier momento para cancelar o HELP para obtener ayuda.' : 'Reply STOP at any time to opt out or HELP for assistance.'}</p>}
            <Link to={path('/')} className="mt-3 inline-block font-bold text-emerald-800 underline">{es ? 'Volver a GIGGONE' : 'Back to GIGGONE'}</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
            <label className="block text-sm font-bold text-slate-700">{c.phoneLabel}
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" placeholder="(909) 555-0123" className={inputCls} />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-bold text-slate-700">{c.nameLabel}
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" className={inputCls} />
              </label>
              <label className="block text-sm font-bold text-slate-700">{c.emailLabel}
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className={inputCls} />
              </label>
            </div>
            <SmsConsentCheckbox checked={sms} onChange={setSms} variant="boxed" id="sms-opt-in-consent" />
            {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <button type="submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-bold text-white hover:bg-orange-600 disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{c.submit}
            </button>
          </form>
        )}
      </div>

      <aside className="space-y-4 lg:col-span-2">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <h2 className="text-sm font-extrabold text-slate-900">{c.receiveHeading}</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
            {SMS_MESSAGE_TYPES[es ? 'es' : 'en'].map((m) => <li key={m}>{m}</li>)}
          </ul>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm leading-relaxed text-slate-700">
          <h2 className="flex items-center gap-2 text-sm font-extrabold text-slate-900"><ShieldCheck className="h-4 w-4 text-emerald-600" />{c.detailsHeading}</h2>
          <ul className="mt-2 space-y-1.5">
            <li><strong>{c.programLabel}</strong> {SMS_PROGRAM_NAME}</li>
            <li>{c.notCondition}</li>
            <li>{c.frequency}</li>
            <li>{c.rates}</li>
            <li>{c.stopHelp}</li>
            <li>{c.supportLabel} <a href={`mailto:${SMS_SUPPORT_EMAIL}`} className="font-semibold text-orange-600 underline">{SMS_SUPPORT_EMAIL}</a></li>
          </ul>
          <p className="mt-3">
            <Link to={path('/privacy')} className="font-semibold text-orange-600 underline">{c.privacy}</Link>
            {' · '}
            <Link to={path('/terms')} className="font-semibold text-orange-600 underline">{c.terms}</Link>
          </p>
        </div>
      </aside>
    </div>
  );
};

export default SmsOptIn;
